import classNames from "classnames";
import Link from "next/link";
import Image from "components/imageNew/image";
import { LinkButton } from "components/simpleButton/linkButton";
import type { SiftedInsightsFeatureCardProps } from "./siftedInsightsFeatureCard.types";
import {
  SIFTED_INSIGHTS_FEATURE_CARD_TYPES,
  SIFTED_INSIGHTS_FEATURE_CARD_STATES,
  SIFTED_INSIGHTS_FEATURE_CARD_VARIANTS,
  TAG_COLORS,
} from "./siftedInsightsFeatureCard.types";
import {
  FEATURE_CARD_VARIANT_STYLES,
  SPONSORSHIP_TYPE_DEFAULTS,
  LINK_DEFAULTS,
  HOVER_NOT_JOINED_DEFAULT_TEXT,
} from "./siftedInsightsFeatureCard.constants";

const SiftedInsightsFeatureCard = ({
  type = SIFTED_INSIGHTS_FEATURE_CARD_TYPES.W_IMG,
  title,
  tag,
  tagColor,
  isSponsored,
  sponsorInfo,
  excerpt,
  leftLabel,
  rightLabel,
  href,
  backgroundImage,
  buttonText,
  hoverNotJoinedText = HOVER_NOT_JOINED_DEFAULT_TEXT,
  variant = SIFTED_INSIGHTS_FEATURE_CARD_VARIANTS.SIFTED,
  state = SIFTED_INSIGHTS_FEATURE_CARD_STATES.DEFAULT,
  className,
  onClick,
  target,
}: SiftedInsightsFeatureCardProps) => {
  const isImageCard = type === SIFTED_INSIGHTS_FEATURE_CARD_TYPES.W_IMG;
  const isTextCard = !isImageCard;
  const isWholeCardLink = Boolean(href && !buttonText);
  const isForcedHover =
    state === SIFTED_INSIGHTS_FEATURE_CARD_STATES.HOVER_NOT_JOINED;
  const isForcedActive = state === SIFTED_INSIGHTS_FEATURE_CARD_STATES.ACTIVE;
  const isForcedFocused = state === SIFTED_INSIGHTS_FEATURE_CARD_STATES.FOCUSED;

  const isForcedState = isForcedHover || isForcedActive || isForcedFocused;

  const visibleSponsorInfo = (sponsorInfo || []).filter(
    (sponsor) => sponsor.sponsorshipType && sponsor.logo?.url
  );

  const interactiveWrapperClasses = classNames(
    "group relative block h-full w-full overflow-hidden text-left",
    {
      "bg-mono-15": isImageCard,
      "bg-mono-00": !isImageCard,
    },
    "focus-visible:outline-none",
    className
  );

  const cardBody = (
    <>
      {isImageCard && (
        <div
          className={classNames(
            "absolute inset-0 h-full w-full",
            backgroundImage?.url ? "bg-mono-00" : "bg-mono-15"
          )}
        >
          {backgroundImage?.url && (
            <Image
              src={backgroundImage.url}
              alt={backgroundImage.alt || ""}
              width={backgroundImage.width}
              height={backgroundImage.height}
              blurDataURL={backgroundImage.blurUpThumb || undefined}
              className="pointer-events-none h-100 w-100 object-cover"
              sizes="290px"
            />
          )}
        </div>
      )}

      <div
        className={classNames("relative flex h-full p-3", {
          "items-end": isImageCard,
          "flex-col items-start": isTextCard,
        })}
      >
        <div
          className={classNames("flex w-full flex-col bg-mono-00", {
            "gap-[6px] p-[14px]": isImageCard,
            "h-full p-0": isTextCard,
            "pt-[46px]": isTextCard && tag,
          })}
        >
          {(leftLabel || rightLabel) && (
            <div
              className="flex min-h-[18px] w-full items-start justify-between gap-3"
              data-testid="insights-feature-card__topMeta"
            >
              <div className="text-assist-14 text-mono-40">{leftLabel}</div>
              <div
                className={classNames(
                  "text-assist-14",
                  FEATURE_CARD_VARIANT_STYLES[variant].rightLabel
                )}
              >
                {rightLabel}
              </div>
            </div>
          )}

          <div
            className={classNames("flex flex-col", {
              "gap-[14px]": isImageCard,
              "h-full gap-sm": isTextCard,
            })}
          >
            {href && buttonText && isImageCard ? (
              <Link
                href={href}
                target={target}
                rel={
                  target === LINK_DEFAULTS.TARGET_EXTERNAL
                    ? LINK_DEFAULTS.REL_EXTERNAL
                    : undefined
                }
                className="w-fit focus-visible:outline-none"
                onClick={onClick}
              >
                <p className="font-recklessBold text-accent-22 text-black-rock">
                  {title}
                </p>
              </Link>
            ) : (
              <p className="font-recklessBold text-black-rock">
                <span
                  className={classNames({
                    "text-accent-22": isImageCard,
                    "text-regular-18": isTextCard,
                  })}
                >
                  {title}
                </span>
              </p>
            )}

            {excerpt && (
              <p
                className={classNames("text-regular-16 text-mono-70", {
                  "line-clamp-3": isImageCard,
                  "line-clamp-4": isTextCard,
                })}
              >
                {excerpt}
              </p>
            )}

            {isSponsored && visibleSponsorInfo.length > 0 && (
              <div className="flex gap-x-[14px]">
                {visibleSponsorInfo.map((sponsor, index) => (
                  <div
                    key={`${sponsor.name || SPONSORSHIP_TYPE_DEFAULTS.SPONSOR_NAME}-${index}`}
                    className="flex flex-col"
                  >
                    <p className="mb-1 text-3xs leading-xl text-mono-30">
                      {sponsor.sponsorshipType}
                    </p>
                    {sponsor.logo && (
                      <div className="h-[32px] w-[80px] min-w-[80px]">
                        <Image
                          src={sponsor.logo.url}
                          alt={
                            sponsor.logo.alt ||
                            `${sponsor.name || SPONSORSHIP_TYPE_DEFAULTS.SPONSOR_ALT_TEXT} logo`
                          }
                          width={sponsor.logo.width}
                          height={sponsor.logo.height}
                          blurDataURL={sponsor.logo.blurUpThumb || undefined}
                          className="pointer-events-none h-100 w-100 object-contain object-center"
                          sizes="80px"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {buttonText && href && isImageCard && (
              <div className="w-full pt-[2px]">
                <LinkButton
                  href={href}
                  size="sm"
                  variant="primary"
                  accent={FEATURE_CARD_VARIANT_STYLES[variant].buttonAccent}
                  className="w-full"
                  target={target}
                  rel={
                    target === LINK_DEFAULTS.TARGET_EXTERNAL
                      ? LINK_DEFAULTS.REL_EXTERNAL
                      : undefined
                  }
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                >
                  {buttonText}
                </LinkButton>
              </div>
            )}

            {buttonText && href && isTextCard && (
              <div className="mt-auto pt-[14px]">
                <Link
                  href={href}
                  target={target}
                  rel={
                    target === LINK_DEFAULTS.TARGET_EXTERNAL
                      ? LINK_DEFAULTS.REL_EXTERNAL
                      : undefined
                  }
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  className={classNames(
                    "block w-full underline decoration-solid font-emphasis-14 [text-decoration-skip-ink:none]",
                    FEATURE_CARD_VARIANT_STYLES[variant].textOnlyAction
                  )}
                >
                  {buttonText}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {tag && (
        <div
          className={classNames(
            "pointer-events-none absolute left-[12px] top-[12px] px-[6px] py-[4px] font-bold text-assist-14 text-mono-00",
            {
              "bg-black-rock": tagColor === TAG_COLORS.STANDARD,
              "bg-mono-40": tagColor === TAG_COLORS.NEUTRAL,
              "bg-sifted": tagColor === TAG_COLORS.SIFTED || !tagColor,
              "bg-pro": tagColor === TAG_COLORS.PRO,
            }
          )}
        >
          {tag}
        </div>
      )}

      {!isForcedState && (
        <div
          data-testid="insights-feature-card__interactiveBorder"
          className={classNames(
            "pointer-events-none absolute inset-0 z-10 border-[3px] border-solid opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100",
            FEATURE_CARD_VARIANT_STYLES[variant].statusBorder
          )}
        />
      )}

      {isForcedActive && (
        <div
          className={classNames(
            "pointer-events-none absolute inset-0 opacity-40",
            FEATURE_CARD_VARIANT_STYLES[variant].statusOverlay
          )}
        />
      )}

      {isForcedFocused && (
        <div
          className={classNames(
            "pointer-events-none absolute inset-0 border-[3px] border-solid",
            FEATURE_CARD_VARIANT_STYLES[variant].statusBorder
          )}
        />
      )}

      {isForcedHover && (
        <div
          className={classNames(
            "pointer-events-none absolute inset-0 border-[3px] border-solid",
            FEATURE_CARD_VARIANT_STYLES[variant].statusBorder
          )}
        >
          <div className="absolute inset-0 bg-mono-00 opacity-95" />
          <div className="relative z-10 flex h-full w-full items-center justify-center p-4">
            <p
              className={classNames(
                "font-emphasis-22",
                FEATURE_CARD_VARIANT_STYLES[variant].statusText
              )}
            >
              {hoverNotJoinedText}
            </p>
          </div>
        </div>
      )}
    </>
  );

  if (isWholeCardLink && href) {
    return (
      <Link
        href={href}
        target={target}
        rel={
          target === LINK_DEFAULTS.TARGET_EXTERNAL
            ? LINK_DEFAULTS.REL_EXTERNAL
            : undefined
        }
        className={interactiveWrapperClasses}
        onClick={onClick}
      >
        {cardBody}
      </Link>
    );
  }

  return (
    <article
      className={interactiveWrapperClasses}
      onClick={onClick}
      data-testid="insights-feature-card"
    >
      {cardBody}
    </article>
  );
};

export default SiftedInsightsFeatureCard;
